import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Card, Button, Spinner, Alert, Table, Badge,
  Modal, Form, InputGroup,
} from 'react-bootstrap';
import NotificationService from '../../Services/NotificationService';

function NotificationGroups() {
  const [groups, setGroups] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [includeInactive, setIncludeInactive] = useState(false);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  // Form states
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDescription, setNewGroupDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Selected group for members/edit
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [newMemberEmail, setNewMemberEmail] = useState('');

  // Edit form states
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');

  const fetchGroups = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await NotificationService.getGroups(includeInactive);
      setGroups(data || []);
    } catch (err) {
      setError('Failed to load notification groups');
    } finally {
      setIsLoading(false);
    }
  }, [includeInactive]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    if (!newGroupName.trim()) return;

    try {
      setIsSubmitting(true);
      await NotificationService.createGroup(newGroupName.trim(), newGroupDescription.trim());
      setShowCreateModal(false);
      setNewGroupName('');
      setNewGroupDescription('');
      fetchGroups();
    } catch (err) {
      setError('Failed to create group');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeactivateGroup = async (groupId) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Are you sure you want to deactivate this group?')) return;

    try {
      await NotificationService.deactivateGroup(groupId);
      fetchGroups();
    } catch (err) {
      setError('Failed to deactivate group');
    }
  };

  const handleOpenMembers = async (group) => {
    setSelectedGroup(group);
    setShowMembersModal(true);
    setMembersLoading(true);

    try {
      const data = await NotificationService.getGroupMembers(group.Id);
      setMembers(data || []);
    } catch (err) {
      setError('Failed to load group members');
    } finally {
      setMembersLoading(false);
    }
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    if (!newMemberEmail.trim() || !selectedGroup) return;

    try {
      setIsSubmitting(true);
      await NotificationService.addGroupMember(selectedGroup.Id, newMemberEmail.trim());
      setNewMemberEmail('');
      // Refresh members
      const data = await NotificationService.getGroupMembers(selectedGroup.Id);
      setMembers(data || []);
    } catch (err) {
      setError('Failed to add member');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveMember = async (memberEmail) => {
    if (!selectedGroup) return;

    try {
      await NotificationService.removeGroupMember(selectedGroup.Id, memberEmail);
      // Refresh members
      const data = await NotificationService.getGroupMembers(selectedGroup.Id);
      setMembers(data || []);
    } catch (err) {
      setError('Failed to remove member');
    }
  };

  const handleOpenEdit = (group) => {
    setSelectedGroup(group);
    setEditName(group.Name || '');
    setEditDescription(group.Description || '');
    setShowEditModal(true);
  };

  const handleUpdateGroup = async (e) => {
    e.preventDefault();
    if (!editName.trim() || !selectedGroup) return;

    try {
      setIsSubmitting(true);
      await NotificationService.updateGroup(selectedGroup.Id, {
        Name: editName.trim(),
        Description: editDescription.trim(),
      });
      setShowEditModal(false);
      fetchGroups();
    } catch (err) {
      setError('Failed to update group');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <Container className="py-4">
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3 text-muted">Loading notification groups...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">
            <i className="fa fa-users me-2" />
            Notification Groups
          </h2>
          <p className="text-muted mb-0">
            Manage groups for sending bulk notifications
          </p>
        </div>
        <Button variant="primary" onClick={() => setShowCreateModal(true)}>
          <i className="fa fa-plus me-2" />
          Create Group
        </Button>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          <i className="fa fa-exclamation-circle me-2" />
          {error}
        </Alert>
      )}

      <Card>
        <Card.Header className="bg-dark text-white d-flex justify-content-between align-items-center">
          <span>
            <i className="fa fa-list me-2" />
            Groups
          </span>
          <Form.Check
            type="switch"
            id="include-inactive"
            label="Show inactive"
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.target.checked)}
            className="text-white mb-0"
          />
        </Card.Header>
        <Card.Body className="p-0">
          {groups.length === 0 ? (
            <div className="text-center text-muted p-5">
              <i className="fa fa-users fa-3x mb-3 opacity-50" />
              <p className="mb-0">No notification groups found</p>
              <Button
                variant="link"
                onClick={() => setShowCreateModal(true)}
              >
                Create your first group
              </Button>
            </div>
          ) : (
            <Table responsive hover className="mb-0">
              <thead className="table-light">
                <tr>
                  <th>Name</th>
                  <th>UUID</th>
                  <th>Description</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {groups.map((group) => (
                  <tr key={group.Id}>
                    <td className="fw-semibold">{group.Name}</td>
                    <td>
                      <code className="small text-muted">{group.Id}</code>
                    </td>
                    <td className="text-muted">{group.Description || '-'}</td>
                    <td>
                      {group.IsActive ? (
                        <Badge bg="success">Active</Badge>
                      ) : (
                        <Badge bg="secondary">Inactive</Badge>
                      )}
                    </td>
                    <td className="text-muted small">
                      {group.CreatedAt ? new Date(group.CreatedAt).toLocaleDateString() : '-'}
                    </td>
                    <td className="text-end">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="me-1"
                        onClick={() => handleOpenMembers(group)}
                        title="Manage members"
                      >
                        <i className="fa fa-user-friends" />
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        className="me-1"
                        onClick={() => handleOpenEdit(group)}
                        title="Edit group"
                      >
                        <i className="fa fa-edit" />
                      </Button>
                      {group.IsActive && (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDeactivateGroup(group.Id)}
                          title="Deactivate group"
                        >
                          <i className="fa fa-ban" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Create Group Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)}>
        <Form onSubmit={handleCreateGroup}>
          <Modal.Header closeButton className="bg-dark text-white">
            <Modal.Title>
              <i className="fa fa-plus me-2" />
              Create Notification Group
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Group Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g., Admin Team, Sales Team"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                required
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Description (optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                placeholder="Brief description of this group"
                value={newGroupDescription}
                onChange={(e) => setNewGroupDescription(e.target.value)}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <>
                  <i className="fa fa-plus me-2" />
                  Create Group
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Edit Group Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Form onSubmit={handleUpdateGroup}>
          <Modal.Header closeButton className="bg-dark text-white">
            <Modal.Title>
              <i className="fa fa-edit me-2" />
              Edit Group
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Group Name</Form.Label>
              <Form.Control
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                required
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <>
                  <i className="fa fa-save me-2" />
                  Save Changes
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Members Modal */}
      <Modal
        show={showMembersModal}
        onHide={() => setShowMembersModal(false)}
        size="lg"
      >
        <Modal.Header closeButton className="bg-dark text-white">
          <Modal.Title>
            <i className="fa fa-user-friends me-2" />
            Members of
            {' '}
            {selectedGroup?.Name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Add member form */}
          <Form onSubmit={handleAddMember} className="mb-4">
            <Form.Label>Add Member</Form.Label>
            <InputGroup>
              <Form.Control
                type="email"
                placeholder="Enter email address"
                value={newMemberEmail}
                onChange={(e) => setNewMemberEmail(e.target.value)}
              />
              <Button variant="primary" type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <i className="fa fa-plus" />
                )}
              </Button>
            </InputGroup>
          </Form>

          {/* Members list */}
          {membersLoading && (
            <div className="text-center py-4">
              <Spinner animation="border" size="sm" />
              <span className="ms-2">Loading members...</span>
            </div>
          )}

          {!membersLoading && members.length === 0 && (
            <div className="text-center text-muted py-4">
              <i className="fa fa-users fa-2x mb-2 opacity-50" />
              <p className="mb-0">No members in this group yet</p>
            </div>
          )}

          {!membersLoading && members.length > 0 && (
            <Table hover size="sm">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Added</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {members.map((member) => (
                  <tr key={member.UserEmail}>
                    <td>
                      <i className="fa fa-user-circle me-2 text-muted" />
                      {member.UserEmail}
                    </td>
                    <td className="text-muted small">
                      {member.AddedAt ? new Date(member.AddedAt).toLocaleDateString() : '-'}
                    </td>
                    <td className="text-end">
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleRemoveMember(member.UserEmail)}
                      >
                        <i className="fa fa-times" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowMembersModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default NotificationGroups;
